"use client";

import * as React from "react";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { cn } from "@baluu/ui/lib/utils";
import type { DeliveryAddress } from "./utils/types";

/**
 * Field keys deliberately match the server's productPurchase deliveryAddress
 * paths (`postCode`, not `postcode`) via the canonical DeliveryAddress type —
 * Mongoose silently drops unknown nested keys, and the lowercase spelling is
 * the exact bug family that lost customer addresses. Payload builders can
 * spread these values straight through.
 */
export type DeliveryAddressValues = Required<
  Pick<DeliveryAddress, "addressLine1" | "city" | "postCode">
> &
  Pick<DeliveryAddress, "addressLine2" | "country">;

export const EMPTY_DELIVERY_ADDRESS: DeliveryAddressValues = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  postCode: "",
  country: "",
};

/** Required fields filled? Mirrors the server's physical-product guard
 * (CreateBasketCheckoutSession rejects addressLine1/city/postCode gaps). */
export function isDeliveryAddressComplete(
  values: DeliveryAddressValues,
  opts: { requireCountry?: boolean } = {}
) {
  return Boolean(
    values.addressLine1 &&
      values.city &&
      values.postCode &&
      (!opts.requireCountry || values.country)
  );
}

type DeliveryAddressFormProps = {
  values: DeliveryAddressValues;
  onChange: (field: keyof DeliveryAddressValues, value: string) => void;
  disabled?: boolean;
  className?: string;
};

function DeliveryAddressForm({
  values,
  onChange,
  disabled = false,
  className,
}: DeliveryAddressFormProps) {
  const handleChange =
    (field: keyof DeliveryAddressValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, e.target.value);
    };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        <Label htmlFor="deliveryAddressLine1">Address line 1 *</Label>
        <Input
          id="deliveryAddressLine1"
          type="text"
          value={values.addressLine1}
          onChange={handleChange("addressLine1")}
          disabled={disabled}
          placeholder="123 Street Name"
          autoComplete="address-line1"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="deliveryAddressLine2">Address line 2</Label>
        <Input
          id="deliveryAddressLine2"
          type="text"
          value={values.addressLine2 ?? ""}
          onChange={handleChange("addressLine2")}
          disabled={disabled}
          placeholder="Flat, suite, etc."
          autoComplete="address-line2"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="deliveryCity">City *</Label>
          <Input
            id="deliveryCity"
            type="text"
            value={values.city}
            onChange={handleChange("city")}
            disabled={disabled}
            placeholder="City"
            autoComplete="address-level2"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="deliveryPostcode">Postcode *</Label>
          <Input
            id="deliveryPostcode"
            type="text"
            value={values.postCode}
            onChange={handleChange("postCode")}
            disabled={disabled}
            placeholder="Postcode"
            autoComplete="postal-code"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="deliveryCountry">Country</Label>
        <Input
          id="deliveryCountry"
          type="text"
          value={values.country ?? ""}
          onChange={handleChange("country")}
          disabled={disabled}
          placeholder="Country"
          autoComplete="country-name"
        />
      </div>
    </div>
  );
}

export { DeliveryAddressForm };
export type { DeliveryAddressFormProps };
